/**
 * WebRTC-first live-call transport.
 *
 * The browser sends microphone media directly to the realtime provider. The
 * existing application WebSocket only brokers the SDP exchange and persists
 * transcripts, so API credentials never reach the browser.
 */
import { getVideocallMicrophoneStream } from './videocallMicrophone';

const CONNECTION_TIMEOUT_MS = 20000;
const DISCONNECTED_GRACE_MS = 5000;

export function supportsWebRTCLiveCall() {
    return typeof window.RTCPeerConnection === 'function' &&
        !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

export function createWebRTCState() {
    return {
        peerConnection: null,
        dataChannel: null,
        remoteAudio: null,
        microphoneStream: null,
        connectionTimeout: null,
        disconnectedTimeout: null,
        connecting: false,
        active: false,
        muted: false,
        responseActive: false,
        openingRequested: false,
        assistantTranscript: '',
        fallbackStarted: false,
    };
}

function clearConnectionTimers(state) {
    if (state.connectionTimeout) clearTimeout(state.connectionTimeout);
    if (state.disconnectedTimeout) clearTimeout(state.disconnectedTimeout);
    state.connectionTimeout = null;
    state.disconnectedTimeout = null;
}

function waitForIceGathering(peerConnection) {
    if (peerConnection.iceGatheringState === 'complete') return Promise.resolve();

    return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            peerConnection.removeEventListener('icegatheringstatechange', onChange);
            resolve();
        };
        const onChange = () => {
            if (peerConnection.iceGatheringState === 'complete') finish();
        };
        peerConnection.addEventListener('icegatheringstatechange', onChange);
        setTimeout(finish, 1500);
    });
}

function extractResponseTranscript(response) {
    const output = response && Array.isArray(response.output) ? response.output : [];
    const fragments = [];
    output.forEach((item) => {
        const content = item && Array.isArray(item.content) ? item.content : [];
        content.forEach((part) => {
            const value = part && (part.transcript || part.text);
            if (value) fragments.push(value);
        });
    });
    return fragments.join(' ').trim();
}

function failToFallback(state, ctx, error) {
    if (state.fallbackStarted) return;
    state.fallbackStarted = true;
    ctx.debugLog('WebRTC unavailable; activating resilient audio fallback', {
        name: error && error.name,
        message: error && error.message,
    });
    stopWebRTCLiveCall(state, { releaseMicrophone: false });
    ctx.onFallback(error);
}

function sendProviderEvent(state, payload, ctx) {
    if (!state.dataChannel || state.dataChannel.readyState !== 'open') {
        throw new Error('Realtime data channel is not open');
    }
    state.dataChannel.send(JSON.stringify(payload));
    ctx.debugLog('WebRTC provider event sent', { type: payload.type });
}

function handleProviderEvent(state, rawEvent, ctx) {
    let event;
    try {
        event = JSON.parse(rawEvent.data);
    } catch (error) {
        ctx.debugLog('Ignoring malformed WebRTC provider event');
        return;
    }

    const eventType = event.type || '';
    if (!eventType.endsWith('.delta')) {
        ctx.debugLog('WebRTC provider event received', { type: eventType });
    }

    if (eventType === 'response.created') {
        state.responseActive = true;
        state.assistantTranscript = '';
        ctx.onResponseStarted();
    } else if (eventType === 'input_audio_buffer.speech_started') {
        ctx.onSpeechStarted();
    } else if (eventType === 'input_audio_buffer.speech_stopped') {
        ctx.onSpeechStopped();
    } else if (eventType === 'conversation.item.input_audio_transcription.completed') {
        const transcript = String(event.transcript || '').trim();
        if (transcript) ctx.onUserTranscript(transcript);
    } else if (
        eventType === 'response.audio_transcript.delta' ||
        eventType === 'response.output_audio_transcript.delta' ||
        eventType === 'response.text.delta'
    ) {
        const delta = String(event.delta || '');
        if (delta) {
            state.assistantTranscript += delta;
            ctx.onAssistantTranscriptDelta(delta);
        }
    } else if (eventType === 'output_audio_buffer.started') {
        ctx.onRemoteAudioStarted();
    } else if (eventType === 'output_audio_buffer.stopped') {
        ctx.onRemoteAudioStopped();
    } else if (eventType === 'response.done') {
        state.responseActive = false;
        const response = event.response || {};
        if (response.status && response.status !== 'completed') {
            ctx.onResponseCancelled(response.status);
            return;
        }
        const transcript = state.assistantTranscript.trim() || extractResponseTranscript(response);
        const usage = response.usage || event.usage || {};
        ctx.onResponseCompleted(transcript, Number(usage.total_tokens || 0));
    } else if (eventType === 'error') {
        const providerError = event.error || {};
        const message = providerError.message || 'Realtime voice service error';
        console.error('[growtype-chat][videocall] WebRTC provider error', providerError);
        ctx.onProviderError(message);
    }
}

export async function startWebRTCLiveCall(state, ctx) {
    if (!supportsWebRTCLiveCall()) {
        failToFallback(state, ctx, new Error('WebRTC is not supported'));
        return false;
    }
    if (state.active || state.connecting) return true;

    state.connecting = true;
    state.fallbackStarted = false;
    ctx.debugLog('WebRTC startup entered', {
        connectionTimeoutMs: CONNECTION_TIMEOUT_MS,
        muted: state.muted,
    });
    ctx.onConnecting();

    try {
        ctx.debugLog('WebRTC requesting shared microphone stream');
        const stream = await getVideocallMicrophoneStream(ctx.debugLog);
        state.microphoneStream = stream;
        stream.getAudioTracks().forEach((track) => { track.enabled = !state.muted; });
        ctx.debugLog('WebRTC microphone stream ready', {
            audioTracks: stream.getAudioTracks().length,
        });

        const peerConnection = new RTCPeerConnection();
        state.peerConnection = peerConnection;
        ctx.debugLog('WebRTC peer connection created');

        const remoteAudio = document.createElement('audio');
        remoteAudio.autoplay = !state.muted;
        remoteAudio.playsInline = true;
        remoteAudio.setAttribute('aria-hidden', 'true');
        remoteAudio.style.display = 'none';
        remoteAudio.muted = state.muted;
        document.body.appendChild(remoteAudio);
        state.remoteAudio = remoteAudio;

        peerConnection.ontrack = (event) => {
            remoteAudio.srcObject = event.streams[0] || new MediaStream([event.track]);
            if (state.muted) {
                if (typeof remoteAudio.pause === 'function') remoteAudio.pause();
                return;
            }
            const playPromise = remoteAudio.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch((error) => {
                    ctx.debugLog('Remote audio autoplay is waiting for user activation', {
                        name: error && error.name,
                    });
                });
            }
        };

        peerConnection.onconnectionstatechange = () => {
            const connectionState = peerConnection.connectionState;
            ctx.debugLog('WebRTC connection state changed', { state: connectionState });
            if (connectionState === 'connected') {
                clearConnectionTimers(state);
                state.connecting = false;
                state.active = true;
                ctx.onConnected();
            } else if (connectionState === 'failed') {
                failToFallback(state, ctx, new Error('WebRTC connection failed'));
            } else if (connectionState === 'disconnected' && !state.disconnectedTimeout) {
                state.disconnectedTimeout = setTimeout(() => {
                    if (peerConnection.connectionState === 'disconnected') {
                        failToFallback(state, ctx, new Error('WebRTC connection was lost'));
                    }
                }, DISCONNECTED_GRACE_MS);
            }
        };

        stream.getAudioTracks().forEach((track) => peerConnection.addTrack(track, stream));

        const dataChannel = peerConnection.createDataChannel('oai-events');
        state.dataChannel = dataChannel;
        dataChannel.onmessage = (event) => handleProviderEvent(state, event, ctx);
        dataChannel.onerror = (event) => {
            console.error('[growtype-chat][videocall] WebRTC data channel error', event);
        };
        dataChannel.onopen = () => {
            ctx.debugLog('WebRTC realtime data channel opened');
            if (!state.openingRequested) {
                state.openingRequested = true;
                const openingInstruction = String(ctx.openingInstruction || '').trim();
                const openingEvent = { type: 'response.create' };
                if (openingInstruction) {
                    openingEvent.response = { instructions: openingInstruction };
                }
                sendProviderEvent(state, openingEvent, ctx);
            }
        };

        const offer = await peerConnection.createOffer({ offerToReceiveAudio: true });
        ctx.debugLog('WebRTC local offer created', { sdpLength: offer && offer.sdp ? offer.sdp.length : 0 });
        await peerConnection.setLocalDescription(offer);
        ctx.debugLog('WebRTC local description applied; waiting for ICE gathering');
        await waitForIceGathering(peerConnection);
        ctx.debugLog('WebRTC ICE gathering wait finished', {
            iceGatheringState: peerConnection.iceGatheringState,
            connectionState: peerConnection.connectionState,
        });

        const localDescription = peerConnection.localDescription;
        if (!localDescription || !localDescription.sdp) {
            throw new Error('Browser did not create a WebRTC SDP offer');
        }

        ctx.sendBrokerOffer(localDescription.sdp);
        ctx.debugLog('WebRTC broker offer sent; connection timeout armed', {
            timeoutMs: CONNECTION_TIMEOUT_MS,
        });
        state.connectionTimeout = setTimeout(() => {
            ctx.debugLog('WebRTC connection timeout fired', {
                active: state.active,
                connecting: state.connecting,
                connectionState: state.peerConnection ? state.peerConnection.connectionState : null,
                dataChannelState: state.dataChannel ? state.dataChannel.readyState : null,
            });
            if (!state.active) failToFallback(state, ctx, new Error('WebRTC connection timed out'));
        }, CONNECTION_TIMEOUT_MS);
        return true;
    } catch (error) {
        failToFallback(state, ctx, error);
        return false;
    }
}

export async function acceptWebRTCAnswer(state, answerSdp, ctx) {
    if (!state.peerConnection || !answerSdp) {
        failToFallback(state, ctx, new Error('Invalid WebRTC answer'));
        return false;
    }

    try {
        await state.peerConnection.setRemoteDescription({ type: 'answer', sdp: answerSdp });
        ctx.debugLog('WebRTC remote description applied');
        return true;
    } catch (error) {
        failToFallback(state, ctx, error);
        return false;
    }
}

export function setWebRTCMicrophoneMuted(state, muted, ctx = null) {
    state.muted = !!muted;
    if (state.microphoneStream) {
        state.microphoneStream.getAudioTracks().forEach((track) => {
            track.enabled = !state.muted;
        });
    }
    if (state.remoteAudio) {
        state.remoteAudio.autoplay = !state.muted;
        state.remoteAudio.muted = state.muted;
        if (state.muted && typeof state.remoteAudio.pause === 'function') {
            state.remoteAudio.pause();
        } else if (!state.muted && typeof state.remoteAudio.play === 'function') {
            const playPromise = state.remoteAudio.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        }
    }
    if (state.muted && state.responseActive && ctx) {
        try {
            sendProviderEvent(state, { type: 'response.cancel' }, ctx);
        } catch (_error) {
            // Remote playback is already silent; cancellation is best-effort.
        }
    }
}

export function interruptWebRTCAssistant(state, ctx = null) {
    if (state.remoteAudio) {
        state.remoteAudio.autoplay = false;
        state.remoteAudio.muted = true;
        if (typeof state.remoteAudio.pause === 'function') state.remoteAudio.pause();
    }
    if (!state.responseActive || !ctx) return false;
    try {
        sendProviderEvent(state, { type: 'response.cancel' }, ctx);
        return true;
    } catch (_error) {
        // Local playback is already silent; cancellation is best-effort.
        return false;
    }
}

export function stopWebRTCLiveCall(state, options = {}) {
    clearConnectionTimers(state);

    if (state.dataChannel) {
        try { state.dataChannel.close(); } catch (error) {}
    }
    if (state.peerConnection) {
        state.peerConnection.ontrack = null;
        state.peerConnection.onconnectionstatechange = null;
        try { state.peerConnection.close(); } catch (error) {}
    }
    if (state.remoteAudio) {
        try { state.remoteAudio.pause(); } catch (error) {}
        state.remoteAudio.srcObject = null;
        if (state.remoteAudio.parentNode) state.remoteAudio.parentNode.removeChild(state.remoteAudio);
    }
    if (options.releaseMicrophone && state.microphoneStream) {
        state.microphoneStream.getTracks().forEach((track) => track.stop());
    }

    state.peerConnection = null;
    state.dataChannel = null;
    state.remoteAudio = null;
    state.microphoneStream = null;
    state.connecting = false;
    state.active = false;
    state.openingRequested = false;
    state.assistantTranscript = '';
    state.responseActive = false;
    if (!options.preserveMuted) state.muted = false;
}
